import { computed, Injectable, signal } from '@angular/core';
import { StorageService } from './storage.service';
import { NGXLogger } from 'ngx-logger';
import { USER_ROLE, UserRoleType } from '../types/user-role.types';
import { UserProfileServiceService } from './profile/user-profile-service.service';
import { AuthService } from './auth.service';

interface ProfileFlags {
  readonly isSellersUser: boolean;
  readonly isBusinessAdmin: boolean;
  readonly isBoss: boolean;
}

const NO_PROFILE_FLAGS: ProfileFlags = { isSellersUser: false, isBusinessAdmin: false, isBoss: false };

function profileFlagsFrom(userData: any): ProfileFlags {
  const isSellersUser = userData?.role?.name === 'sellers';
  return {
    isSellersUser,
    isBusinessAdmin: isSellersUser && userData.business_admin === 1,
    isBoss: isSellersUser && userData.is_boss === true,
  };
}

@Injectable({
  providedIn: 'root'
})

export class SelectionStateService {

  private readonly STORAGE_KEY = 'selected_role';
  private readonly STORAGE_KEY_EVENT = 'active_event_id';

  private _selectedRole = signal<UserRoleType>('unknown');
  public selectedRole = computed(() => this._selectedRole());

  /** True when the active role is administrator. Single source of truth for `isAdmin` checks. */
  public isAdministrator = computed(() => this._selectedRole() === USER_ROLE.ADMINISTRATOR);

  /** Evento en el que vale el rol activo; null en pantallas globales. */
  private _activeEventId = signal<number | null>(null);
  public activeEventId = computed(() => this._activeEventId());

  private _profileFlags = signal<ProfileFlags>(NO_PROFILE_FLAGS);
  /** Usuario con rol `sellers` en `/me` (no recepcionista ni mobile admin). */
  public isSellersUser = computed(() => this._profileFlags().isSellersUser);
  public isBusinessAdmin = computed(() => this._profileFlags().isBusinessAdmin);
  public isBoss = computed(() => this._profileFlags().isBoss);
  /** Rol con el que se entra a Administración, según `/me`. */
  public administrationRole = computed<UserRoleType | null>(() => {
    const flags = this._profileFlags();
    if (flags.isBusinessAdmin) {
      return USER_ROLE.ADMINISTRATOR;
    }
    return flags.isBoss ? USER_ROLE.SALES_MANAGER : null;
  });

  private _availableRoles = signal<UserRoleType[]>([]);
  public availableRoles = computed(() => this._availableRoles());

  private _initialized = signal(false);
  public isInitialized = computed(() => this._initialized());

  /**
   * Inicialización en vuelo. Se comparte entre `app.component`, el
   * `RedirectingComponent` y los guards: todos esperan la MISMA corrida en vez
   * de disparar cada uno su propio `/me`.
   */
  private _initializing: Promise<void> | null = null;

  /** Marca que alguien ya arrancó (o completó) la restauración del rol. */
  private _roleRestoreStarted = false;
  private _resolveRoleRestored: () => void = () => undefined;
  /**
   * Se resuelve — nunca se rechaza — cuando el rol persistido ya fue leído de
   * storage, haya rol guardado o no.
   */
  private readonly _roleRestored = new Promise<void>((resolve) => {
    this._resolveRoleRestored = resolve;
  });

  constructor(
    private _storage: StorageService,
    private _logger: NGXLogger,
    private _userProfileService: UserProfileServiceService,
    private _authService: AuthService,
  ) { }

  /**
   * Idempotente mientras está en vuelo: los distintos puntos de arranque
   * (`app.component`, `RedirectingComponent`, `RoleReadyGuard`) comparten una
   * única corrida en lugar de pedir `/me` cada uno por su cuenta.
   */
  public async initialize(): Promise<void> {
    if (this._initializing) {
      return this._initializing;
    }

    this._roleRestoreStarted = true;
    const run = this.runInitialize();
    this._initializing = run;

    try {
      await run;
    } finally {
      if (this._initializing === run) {
        this._initializing = null;
      }
    }
  }

  private async runInitialize(): Promise<void> {
    // El rol persistido PRIMERO: es una lectura local (storage) y de él depende
    // el scope de los reportes. Leerlo después del `/me` dejaba una ventana del
    // tamaño de un round-trip en la que una pantalla de reportes montada en
    // frío pedía datos sin el param `role`; el back, que falla abierto, devolvía
    // toda la productora en vez del local del jefe de ventas.
    await this.restoreSelectedRole();

    try {
      let userData: any = null;

      try {
        userData = await this._userProfileService.loadProfileAsync();
      } catch (apiError) {
        this._logger.warn('API not available, trying storage fallback', apiError);
        userData = await this._userProfileService.initializeFromStorage();
      }

      if (userData) {
        await this.calculateAvailableRoles(userData);
      } else {
        this._availableRoles.set(['unknown']);
      }
    } catch (e) {
      this._logger.error('Init selection state failed', e);
      this._availableRoles.set(['unknown']);
    }

    this._initialized.set(true);
  }

  /**
   * Lee el rol y el evento activo persistidos. Nunca propaga: destraba la espera pase lo que pase.
   * Ambas claves se leen antes de destrabar, para que los guards vean el contexto completo.
   */
  private async restoreSelectedRole(): Promise<void> {
    try {
      const [stored, storedEventId] = await Promise.all([
        this._storage.get(this.STORAGE_KEY),
        this._storage.get(this.STORAGE_KEY_EVENT),
      ]);
      if (stored) {
        this._selectedRole.set(stored);
        this._logger.debug('Restored selected role from storage', stored);
      }
      this._activeEventId.set(typeof storedEventId === 'number' ? storedEventId : null);
    } catch (e) {
      this._logger.error('Restore selected role from storage failed', e);
    } finally {
      this._resolveRoleRestored();
    }
  }

  /**
   * Punto de espera para los guards de las pantallas cuyo scope depende del rol.
   * Resuelve cuando el rol persistido está leído (haya rol o no) y nunca se
   * rechaza: si el `/me` o el storage fallan, la navegación sigue igual — con el
   * rol que se haya podido determinar — en vez de colgar la app.
   *
   * Si nadie arrancó la inicialización todavía (deep link a un reporte antes de
   * que corra `app.component`), la arranca acá: esperar sin arrancarla sería un
   * deadlock.
   */
  public whenRoleRestored(): Promise<void> {
    if (!this._roleRestoreStarted) {
      void this.initialize();
    }

    return this._roleRestored;
  }

  /**
   * Punto de espera para los guards que leen los flags de `/me` (`isSellersUser`, ...):
   * a diferencia de `whenRoleRestored()`, espera la inicialización completa. Ya inicializado,
   * resuelve sin volver a pedir `/me`; en vuelo, se engancha a la misma corrida.
   */
  public whenProfileReady(): Promise<void> {
    return this._initialized() ? Promise.resolve() : this.initialize();
  }

  public markAsInitialized(): void {
    this._initialized.set(true);
    this._roleRestoreStarted = true;
    this._resolveRoleRestored();
    this._logger.debug('Marked as initialized without API calls');
  }

  public async isUserLoggedIn(): Promise<boolean> {
    return await this._authService.isLoggedIn();
  }

  public async setSelectedRole(role: UserRoleType): Promise<void> {
    this._selectedRole.set(role);
    await this._storage.set(this.STORAGE_KEY, role);
    this._logger.debug('Selected role saved', role);
  }

  /** Entra a un evento con un rol: el rol vale solo para ese evento. */
  public async setEventContext(role: UserRoleType, eventId: number): Promise<void> {
    this._selectedRole.set(role);
    this._activeEventId.set(eventId);
    await Promise.all([
      this._storage.set(this.STORAGE_KEY, role),
      this._storage.set(this.STORAGE_KEY_EVENT, eventId),
    ]);
  }

  /** Pantallas globales (dashboard, lista, tickets...): rol `seller` sin evento. */
  public setGlobalContext(): Promise<void> {
    return this.setRoleWithoutEvent(USER_ROLE.SELLER);
  }

  /**
   * Para pantallas que se muestran antes de dejar el evento (lista de eventos): vuelve al contexto
   * global sin depender del guard. No cambia nada si no es `sellers` o si ya está en ese contexto.
   */
  public async leaveEventContext(): Promise<void> {
    await this.whenProfileReady();
    const alreadyGlobal = this._selectedRole() === USER_ROLE.SELLER && this._activeEventId() === null;
    if (this.isSellersUser() && !alreadyGlobal) {
      await this.setGlobalContext();
    }
  }

  /** Administración: el rol que le da `/me` (`administrationRole()`), sin evento. */
  public setAdministrationContext(role: UserRoleType): Promise<void> {
    return this.setRoleWithoutEvent(role);
  }

  private async setRoleWithoutEvent(role: UserRoleType): Promise<void> {
    this._selectedRole.set(role);
    this._activeEventId.set(null);
    await Promise.all([
      this._storage.set(this.STORAGE_KEY, role),
      this._storage.remove(this.STORAGE_KEY_EVENT),
    ]);
  }

  public async clear(): Promise<void> {
    this._selectedRole.set('unknown');
    this._activeEventId.set(null);
    this._availableRoles.set([]);
    this._profileFlags.set(NO_PROFILE_FLAGS);
    await Promise.all([
      this._storage.remove(this.STORAGE_KEY),
      this._storage.remove(this.STORAGE_KEY_EVENT),
    ]);
  }

  public async calculateAvailableRoles(userData: any): Promise<UserRoleType[]> {
    const roles: UserRoleType[] = [];
    this._profileFlags.set(profileFlagsFrom(userData));

    if (!userData || !userData.role) {
      this._availableRoles.set(['unknown']);
      return ['unknown'];
    }

    if (userData.role.name === 'sellers') {
      if (userData.business_admin === 1) {
        roles.push(USER_ROLE.ADMINISTRATOR);
        roles.push(USER_ROLE.SCANNER);
      }
      roles.push('seller');

      if (userData.is_boss === true) {
        roles.push('sales-manager');
      }
    }

    if (userData.role.name === 'recepcionist') {
      roles.push('recepcionist');
    }

    if (userData.role.name === 'mobile_app_admin') {
      roles.push('mobile_app_admin');
    }

    const result: UserRoleType[] = roles.length ? roles : ['unknown'];
    this._availableRoles.set(result);
    this._logger.debug('Available roles set from /me', result);

    return result;
  }

  getSelectedRoleValue(): UserRoleType {
    return this._selectedRole();
  }

  public async getAndRefreshRoles(): Promise<void> {
    try {
      const userData = await this._userProfileService.loadProfileAsync();

      await this.calculateAvailableRoles(userData);
    } catch (e) {
      this._logger.error('getAndRefreshRoles failed', e);
      this._availableRoles.set(['unknown']);
    }
  }
}
